import _ from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as icons from "@fortawesome/free-regular-svg-icons";
import { generateClient } from "@aws-amplify/api";
import { getAgentSettings } from "@/graphql/queries";
import { upsertAgentSetting } from "@/graphql/mutations";
import useBodyClick from "@/hooks/useBodyClick";
import useArrowKeys from "@/hooks/useArrowKeys";
import useLoadMore from "@/hooks/useLoadMore";
import useSubscription from "@/hooks/useSubscription";
import Header from "@/Header";
import { dash, downIcon, SpinnerLineDark, upIcon } from "@/utils";
import { getListSort, setListSort } from "@/utils/stickySettings";
import AIPlatformsList from "../AIPlatformsList";

const client = generateClient();

export default () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isPlatformsTab = location.pathname.includes("/ai_platforms");
  const activeTab = isPlatformsTab ? "platforms" : "settings";

  const [busy, setBusy] = useState({});
  const updateBusy = useCallback((id, value) => {
    setBusy((prevState) => ({ ...prevState, [id]: value }));
  }, []);

  const [search, setSearch] = useState("");
  const [searchCount, setSearchCount] = useState(0);
  const incSearchCount = useCallback(() => setSearchCount((p) => p + 1), []);
  const debounceSearch = useCallback(
    _.debounce((v) => {
      setDefaults();
      setSearch(v);
    }, 500),
    [],
  );

  const { field: defaultSortField, order: defaultSortOrder } = useMemo(
    () =>
      getListSort({ key: window.location.pathname }) || {
        field: "updated_at",
        order: "desc",
      },
    [],
  );
  const [sortField, setSortField] = useState(
    defaultSortField && defaultSortOrder ? defaultSortField : undefined,
  );
  const [sortOrder, setSortOrder] = useState(
    defaultSortField && defaultSortOrder
      ? { [defaultSortField]: defaultSortOrder }
      : {},
  );

  const filters_ref = useRef();
  filters_ref.current = useMemo(() => {
    const s = (search || "").trim();

    let filters = null;

    if (s) {
      filters = {
        agent_name: { like: `%${s}%` },
      };
    }

    const sorting = [
      { field: sortField, order: sortOrder[sortField] },
      { field: "id", order: "asc" },
    ];

    return { filters, sorting };
  }, [search, sortField, sortOrder]);

  const {
    isLoadingMore,
    limit,
    loadMoreButton,
    offset,
    rows,
    setDefaults,
    setIsLoadingMore,
    setRows,
    setTotal,
  } = useLoadMore({ position: "absolute" });
  useSubscription({
    filters_ref,
    limit_ref: limit,
    offset_ref: offset,
    rankings_key: "agent_settings",
    setRows,
  });

  const [sidebarItem, _setSidebarItem] = useState();
  const setSidebarItem = useCallback((item) => {
    _setSidebarItem(item);
    const queryParams = new URLSearchParams(window.location.search);
    if (!item) {
      queryParams.delete("agent_setting_id");
    } else {
      queryParams.set("agent_setting_id", item.id);
    }
    navigate(
      {
        pathname: window.location.pathname,
        search: `?${queryParams.toString()}`,
      },
      { replace: true },
    );
  }, []);

  const [loadCount, setLoadCount] = useState(0);
  const incLoadCount = () => setLoadCount((prevState) => prevState + 1);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const agent_setting_id = parseInt(
      queryParams.get("agent_setting_id") || undefined,
      10,
    );
    if (agent_setting_id) {
      const item = rows.find((e) => e.id === agent_setting_id);
      if (item) setSidebarItem(item);
    }
  }, [rows]);

  const load = useCallback(async () => {
    const { filters, sorting } = filters_ref.current;
    const res = await client.graphql({
      query: getAgentSettings,
      variables: { filters, limit, offset, sorting },
    });
    const payload = res?.data?.getAgentSettings;
    const data = payload?.agent_settings ?? [];
    setRows((prevState) => (offset === 0 ? data : [...prevState, ...data]));
    setTotal(payload?.total_count ?? data.length);
    incLoadCount();
    setIsLoadingMore(false);
  }, [filters_ref.current, limit, offset]);

  useEffect(() => {
    if (activeTab === "settings") {
      load().catch(console.error);
    }
  }, [search, offset, load, activeTab]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  useBodyClick({ onClick: setSidebarItem });
  useArrowKeys({
    rows,
    setSidebarRow: setSidebarItem,
    sidebarRow: sidebarItem,
  });

  const handleDelete = useCallback(
    async (row) => {
      const ok = window.confirm(`Delete agent setting "${row.agent_name}"?`);
      if (!ok) return;
      try {
        updateBusy(row.id, true);
        if (row.id === sidebarItem?.id) setSidebarItem();
        await client.graphql({
          query: upsertAgentSetting,
          variables: {
            input: { agent_settings: { id: row.id } },
            input_delete: [{ id: row.id, key: "agent_settings" }],
          },
        });
        setRows((prev) => prev.filter((r) => r.id !== row.id));
        setTotal((t) => Math.max(0, t - 1));
      } catch (e) {
        alert("Delete failed");
      } finally {
        updateBusy(row.id, false);
      }
    },
    [sidebarItem],
  );

  const handleSort = (field) => () => {
    const order =
      sortField === field && sortOrder[field] === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder({ [field]: order });
    setListSort({ field, key: window.location.pathname, order });
    setDefaults();
  };

  const sortIcon = sortOrder[sortField] === "asc" ? upIcon : downIcon;

  const stickyThStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: "#F4F4F4",
    zIndex: 2,
    fontSize: "0.875rem",
    textAlign: "left",
  };
  const idCellStyle = {
    borderRight: "1px solid #ccc",
    padding: "0 12px",
    width: 90,
  };
  const nameCellStyle = {
    borderRight: "1px solid #ccc",
    padding: "0 12px",
    maxWidth: 200,
  };
  const lastCellStyle = { padding: "0 12px", width: 220 };

  const agent_breakout = (item) => (
    <div onClick={(event) => event.stopPropagation()}>
      <Link
        to={`/app/customers/agent_settings/${item.id}`}
        className="text-decoration-none fg-purple"
      >
        <h5 className="mb-3 fg-purple text-break">
          <b>{item.agent_name}</b>
        </h5>
      </Link>
      <div className="small text-muted mb-1">Executable</div>
      <div className="mb-3 text-break">{item.agent_executable || dash}</div>
      <div className="row g-0 mb-2">
        <div className="col">Priority</div>
        <div className="col-auto">
          <b>{item.priority ?? dash}</b>
        </div>
      </div>
    </div>
  );

  const table = useMemo(
    () => (
      <div className="row g-0">
        <div className="col pe-0">
          <div
            className="border rounded-3 overflow-auto"
            style={{ maxHeight: `calc(100vh - 150px)` }}
          >
            <table
              className="table table-sm table-borderless align-middle mb-0"
              style={{ width: "100%", fontSize: "0.875rem" }}
            >
              <thead className="text-muted small" style={{ height: "30px" }}>
                <tr className="border-bottom">
                  <th
                    style={stickyThStyle}
                    className="ps-3 cursor-pointer"
                    onClick={handleSort("id")}
                  >
                    ID{sortField === "id" && sortIcon}
                  </th>
                  <th
                    style={stickyThStyle}
                    className="ps-3 cursor-pointer"
                    onClick={handleSort("agent_name")}
                  >
                    Agent Name{sortField === "agent_name" && sortIcon}
                  </th>
                  <th
                    style={stickyThStyle}
                    className="ps-3 cursor-pointer"
                    onClick={handleSort("ai_platform")}
                  >
                    Platform{sortField === "ai_platform" && sortIcon}
                  </th>
                  <th
                    style={stickyThStyle}
                    className="ps-3 cursor-pointer"
                    onClick={handleSort("updated_at")}
                  >
                    Updated At{sortField === "updated_at" && sortIcon}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-bottom ${sidebarItem?.id === r.id ? "bw-focus-ring" : ""}`}
                    onClick={() => setSidebarItem(r)}
                    style={{ height: "30px" }}
                  >
                    <td
                      className="text-start align-middle py-2 ps-3"
                      style={idCellStyle}
                    >
                      <Link
                        to={`/app/customers/agent_settings/${r.id}`}
                        className="text-decoration-none "
                        style={{ color: "inherit" }}
                      >
                        {r.id} {busy[r.id] && <SpinnerLineDark />}
                      </Link>
                    </td>
                    <td className="ps-3 text-truncate" style={nameCellStyle}>
                      <Link
                        to={`/app/customers/agent_settings/${r.id}`}
                        className="text-decoration-none fg-purple"
                      >
                        <b>{r.agent_name}</b>
                      </Link>
                    </td>
                    <td className="ps-3">{r.ai_platform || dash}</td>
                    <td className="ps-3" style={lastCellStyle}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                          {r.updated_at ? moment(r.updated_at).fromNow() : dash}
                        </span>
                        <div>
                          <Link
                            to={`/app/customers/agent_settings/${r.id}`}
                            className="me-3 text-inherit"
                          >
                            <FontAwesomeIcon
                              icon={icons.faPenToSquare}
                              style={{ color: "gray" }}
                            />
                          </Link>
                          <FontAwesomeIcon
                            icon={icons.faTrashCan}
                            className="cursor-pointer text-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(r);
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {sidebarItem && (
          <div className="col-auto ps-4 width_300">
            <div className="position-sticky" style={{ top: 0 }}>
              <button
                className="btn-close float-end"
                onClick={() => setSidebarItem()}
              ></button>
              {agent_breakout(sidebarItem)}
            </div>
          </div>
        )}
      </div>
    ),
    [loadCount, sidebarItem, rows, busy],
  );

  return (
    <div className="container-fluid px-4">
      <Header path="/app/customers/">
        <div className="row align-items-center mt-3 mb-0 g-0">
          <div className="col d-flex align-items-center">
            {/* Agent Settings Tab */}
            <div
              className="ps-4 cursor-pointer"
              onClick={() => navigate("/app/customers/agent_settings")}
            >
              <div
                style={{
                  borderBottom:
                    activeTab === "settings"
                      ? "3px solid #007bff"
                      : "3px solid transparent",
                  paddingBottom: "8px",
                  marginBottom: "10px",
                }}
              >
                <h4
                  className={`mb-0 ${activeTab === "settings" ? "fw-bold" : "text-muted"}`}
                  style={{
                    color: activeTab === "settings" ? "#2E2868" : "#6c757d"
                  }}
                >
                  Agent Settings
                </h4>
              </div>
            </div>

            <h4
              className="mb-0 mx-3 text-muted"
              style={{ opacity: 0.5, paddingBottom: "15px" }}
            >
              {" "}
              |{" "}
            </h4>

            {/* AI Platforms Tab */}
            <div
              className="cursor-pointer"
              onClick={() =>
                navigate("/app/customers/agent_settings/ai_platforms")
              }
            >
              <div
                style={{
                  borderBottom:
                    activeTab === "platforms"
                      ? "3px solid #007bff"
                      : "3px solid transparent",
                  paddingBottom: "8px",
                  marginBottom: "5px",
                }}
              >
                <h4
                  className={`mb-0 ${activeTab === "platforms" ? "fw-bold" : "text-muted"}`}
                  style={{
                    color: activeTab === "platforms" ? "#2E2868" : "#6c757d"
                  }}
                >
                  AI Platforms
                </h4>
              </div>
            </div>
          </div>

          {/* Search Input Section */}
          <div className="col-auto ms-auto pe-0 position-relative">
            <input
              autoFocus
              key={searchCount}
              className="form-control width_200"
              placeholder={`Search ${activeTab === "settings" ? "Agents" : "Platforms"}...`}
              defaultValue={search}
              onChange={(e) => debounceSearch(e.target.value)}
            />
            <i
              className="bi bi-x position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSearch("");
                incSearchCount();
              }}
              role="button"
            ></i>
          </div>

          {/* Create Button */}
          <div className="col-auto">
            <Link
              to={
                activeTab === "settings"
                  ? "/app/customers/agent_settings/new"
                  : "/app/customers/agent_settings/ai_platforms/new"
              }
            >
              <button
                className="btn btn-primary"
                style={{
                  backgroundColor: "#2E2868",
                  borderColor: "#2E2868",
                  marginLeft: "20px",
                }}
              >
                Create New{" "}
                {activeTab === "settings" ? "Agent Setting" : "Platform"}
              </button>
            </Link>
          </div>
        </div>
        <hr
          className="mt-3"
          style={{ borderTop: "1px solid #dee2e6", opacity: 1 }}
        />
      </Header>
      {activeTab === "settings" ? (
        rows?.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
          >
            {table}
            {loadMoreButton}
          </motion.div>
        )
      ) : (
        <AIPlatformsList search={search} />
      )}
    </div>
  );
};
